import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router";
import { Col } from "reactstrap";
import {
  getFormattedContactNumber,
  getFormattedOptions,
  Toast,
} from "utility/helpers";
import { fetchAllAgencies } from "services/services.projects";
import {
  createRegionalDirector,
  editRegionalDirector,
} from "services/services.directory";
import {
  FormContainer,
  FormHeaderSection,
  FormSection,
  HeaderText,
} from "assets/js/Styles";
import useRegions from "hooks/useRegions";
import DirectorForm from "./DirectorForm";
import { POSITION_REGIONAL_DIRECTOR } from "../constants";

const DirectorDetails = () => {
  const location = useLocation();
  const director = location.state?.director || null;
  const agencyDropdown = location.state?.agency || null;

  const [agencyId, setAgencyId] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [agencyOptions, setAgencyOptions] = useState([]);
  const [selectedRegion, setSelectedRegion] = useState(null);
  const [selectedAgency, setSelectedAgency] = useState(null);

  const { options: regionOptions } = useRegions();

  const [formFields, setFormFields] = useState({
    lastName: "",
    firstName: "",
    mobile: "",
    region: "",
    cosFirstName: "",
    cosLastName: "",
    cosNumber: "",
    agency: "",
  });

  const navigate = useNavigate();

  const validate = () => {
    if (
      formFields.cosNumber?.length > 2 &&
      !/^(09)\d{9}$/.test(formFields?.cosNumber)
    ) {
      return "Invalid cos contact number.";
    }

    return true;
  };

  const setDirectorDetails = () => {
    const region = regionOptions.find((reg) => reg.id === director.region.id);

    setFormFields({
      ...formFields,
      lastName: director.lastName || director.name,
      firstName: director.firstName,
      agency: director.agency.id,
      mobile: getFormattedContactNumber(director.contact),
      region,
      cosFirstName: director.cosFirstName,
      cosLastName: director.cosLastName || director.cosName,
      cosNumber: getFormattedContactNumber(director.cosContact),
    });
  };

  const sendData = (formData) => {
    if (director) {
      editRegionalDirector({
        url: `/regional-director/${director.id}`,
        formData,
        callback: ({ isSuccess }) => {
          if (isSuccess) {
            setIsLoading(false);

            navigate("/directory", {
              state: {
                view: POSITION_REGIONAL_DIRECTOR,
                agencySelect: agencyId,
              },
            });

            Toast({
              type: 1,
              content: "Successfully updated regional director details.",
            });
          }
        },
      });
    } else {
      createRegionalDirector({
        formData,
        callback: ({ isSuccess }) => {
          if (isSuccess) {
            setIsLoading(false);

            navigate("/directory", {
              state: {
                view: POSITION_REGIONAL_DIRECTOR,
                agencySelect: agencyId,
              },
            });

            Toast({
              type: 1,
              content: "Successfully created regional director.",
            });
          }
        },
      });
    }
  };

  const handleOnSubmit = async () => {
    const isValid = await validate();

    if (typeof isValid === "boolean") {
      const formData = {
        name: `${formFields.firstName} ${formFields.lastName}`,
        lastName: formFields.lastName,
        firstName: formFields.firstName,
        contact: formFields.mobile.slice(1),
        agencyId: selectedAgency.value,
        regionId: formFields.region.value,
        cosFirstName: formFields.cosFirstName,
        cosLastName: formFields.cosLastName,
      };

      if (formFields.cosNumber) {
        formData.cosContact = formFields.cosNumber.slice(1);
      }
      setIsLoading(true);
      sendData(formData);
    } else {
      Toast({
        type: 4,
        content: isValid,
      });
    }
  };

  const fetchData = () => {
    setIsLoading(true);

    fetchAllAgencies({
      callback: (data) => {
        const options = getFormattedOptions("name", data);

        setAgencyOptions(options);
      },
    });

    setIsLoading(false);
  };

  const setAgencyDropdown = () => {
    const agency = agencyOptions.find((_agency) => _agency.id === agencyId);

    if (agency) setSelectedAgency(agency);
  };

  useEffect(() => {
    //  fetch all regions, agency
    fetchData();
  }, []);

  useEffect(() => {
    if (director) {
      setAgencyId(director.agency.id);
    } else {
      setAgencyId(agencyDropdown);
    }

    if (director !== null && regionOptions.length) {
      setDirectorDetails();
    }

    if (agencyOptions.length) {
      setAgencyDropdown();
    }
  }, [regionOptions, agencyOptions]);

  return (
    <FormContainer>
      <Col className="container">
        <FormHeaderSection>
          <HeaderText>
            {!director ? "Add Regional Director" : "Regional Director Details"}
          </HeaderText>
        </FormHeaderSection>

        <FormSection>
          <DirectorForm
            handleOnSubmit={handleOnSubmit}
            formFields={formFields}
            setFormFields={setFormFields}
            regionOptions={regionOptions}
            selectedRegion={selectedRegion}
            setSelectedRegion={setSelectedRegion}
            agencyOptions={agencyOptions}
            selectedAgency={selectedAgency}
            setAgencyId={setAgencyId}
            setSelectedAgency={setSelectedAgency}
            isLoading={isLoading}
            agencyId={agencyId}
          />
        </FormSection>
      </Col>
    </FormContainer>
  );
};

export default DirectorDetails;
